import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { PageTitle } from "@/components/ui/PageTitle";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EggProductionForm } from "@/components/egg-production/EggProductionForm";
import { EggProductionList } from "@/components/egg-production/EggProductionList";
import { EggProductionStats } from "@/components/egg-production/EggProductionStats";
import { useOwnerData } from "@/hooks/useOwnerData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const EggProduction = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [stockItemId, setStockItemId] = useState<string | null>(null);
  const { data: ownerData } = useOwnerData();

  useEffect(() => {
    const fetchStockItemId = async () => {
      if (!ownerData?.business_type_id) return;

      const { data: businessStock } = await supabase
        .from('business_stocks')
        .select('id')
        .eq('business_id', ownerData.business_type_id)
        .single();

      if (!businessStock) {
        toast.error("Erreur: Stock non trouvé");
        return;
      }

      const { data: stockItems } = await supabase
        .from('stock_items')
        .select('id')
        .eq('stock_id', businessStock.id)
        .limit(1);

      if (stockItems && stockItems.length > 0) {
        setStockItemId(stockItems[0].id);
      } else {
        toast.error("Aucun lot de volailles trouvé");
      }
    };

    fetchStockItemId();
  }, [ownerData]);

  if (!stockItemId) {
    return (
      <DashboardLayout userType="owner">
        <div className="space-y-6">
          <PageTitle
            title="Production d'œufs"
            subtitle="Gérez la production d'œufs de votre ferme"
          />
          <p className="text-center py-8">Chargement...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="owner">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <PageTitle
            title="Production d'œufs"
            subtitle="Gérez la production d'œufs de votre ferme"
          />
          <Button onClick={() => setIsDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle production
          </Button>
        </div>

        <EggProductionStats stockItemId={stockItemId} />

        <Card>
          <CardContent className="p-6">
            <EggProductionList stockItemId={stockItemId} />
          </CardContent>
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Enregistrer une production</DialogTitle>
            </DialogHeader>
            <EggProductionForm
              stockItemId={stockItemId}
              onSuccess={() => setIsDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default EggProduction;